import React, { useEffect, useRef } from "react";
import * as THREE from "three";
import logo from "./assets/img/logo/logo.jpeg";

const BrandStudioLoader = () => {
  const mountRef = useRef(null);

  useEffect(() => {
    const container = mountRef.current;
    if (!container) return;

    // Scene setup
    const scene = new THREE.Scene();
    scene.background = new THREE.Color(0x1a1715); // Brand Dark Background

    const camera = new THREE.PerspectiveCamera(
      75,
      window.innerWidth / window.innerHeight,
      0.1,
      1000
    );
    camera.position.z = 5;

    const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
    renderer.setSize(window.innerWidth, window.innerHeight);
    renderer.setPixelRatio(window.devicePixelRatio);
    container.appendChild(renderer.domElement);

    // Create a ground grid for "Manufacturing/Blueprint" feel
    const gridHelper = new THREE.GridHelper(20, 40, 0xc85716, 0x333333);
    gridHelper.position.y = -2;
    gridHelper.rotation.x = Math.PI / 10;
    scene.add(gridHelper);

    // Create some floating abstract geometry (representing precision/parts)
    const geometry = new THREE.IcosahedronGeometry(1, 1);
    const material = new THREE.MeshBasicMaterial({
      color: 0xc85716,
      wireframe: true,
      transparent: true,
      opacity: 0.3,
    });
    const icosahedron = new THREE.Mesh(geometry, material);
    scene.add(icosahedron);

    // Add another inner solid but faint one
    const innerGeometry = new THREE.IcosahedronGeometry(0.5, 0);
    const innerMaterial = new THREE.MeshBasicMaterial({
      color: 0xc85716,
      transparent: true,
      opacity: 0.1,
    });
    const innerIcosahedron = new THREE.Mesh(innerGeometry, innerMaterial);
    scene.add(innerIcosahedron);

    // Animation loop
    const animate = () => {
      requestAnimationFrame(animate);

      icosahedron.rotation.x += 0.005;
      icosahedron.rotation.y += 0.005;
      
      innerIcosahedron.rotation.x -= 0.01;
      innerIcosahedron.rotation.y -= 0.01;

      gridHelper.position.z += 0.02;
      if (gridHelper.position.z > 1) gridHelper.position.z = 0;

      renderer.render(scene, camera);
    };

    animate();

    const handleResize = () => {
      camera.aspect = window.innerWidth / window.innerHeight;
      camera.updateProjectionMatrix();
      renderer.setSize(window.innerWidth, window.innerHeight);
    };

    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
      if (container && renderer.domElement) {
        container.removeChild(renderer.domElement);
      }
      geometry.dispose();
      material.dispose();
      innerGeometry.dispose();
      innerMaterial.dispose();
      renderer.dispose();
    };
  }, []);

  return (
    <div
      ref={mountRef}
      style={{
        width: "100vw",
        height: "100vh",
        backgroundColor: "#1a1715",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        position: "relative",
        overflow: "hidden",
      }}
    >
      {/* Overlay Content */}
      <div
        style={{
          position: "absolute",
          zIndex: 10,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: "20px",
          pointerEvents: "none",
        }}
      >
        <div style={{ position: 'relative' }}>
            <img 
                src={logo} 
                alt="Cart Studio" 
                style={{ 
                    width: '120px', 
                    height: '120px', 
                    borderRadius: '50%',
                    border: '3px solid #c85716',
                    boxShadow: '0 0 20px rgba(200, 87, 22, 0.4)',
                    animation: 'pulseLogo 2s infinite ease-in-out'
                }} 
            />
        </div>
        
        <div style={{ textAlign: "center" }}>
          <h1
            style={{
              fontSize: "3rem",
              fontWeight: "900",
              color: "#fff",
              margin: 0,
              letterSpacing: "0.3em",
              textTransform: "uppercase",
              fontFamily: "'Inter', sans-serif",
            }}
          >
            CART <span style={{ color: "#c85716" }}>STUDIO</span>
          </h1>
          <p style={{ color: "#888", letterSpacing: '0.5em', marginTop: '10px', fontSize: '12px' }}>
            MANUFACTURING PRECISION
          </p>
        </div>

        {/* Cinematic Loading Bar */}
        <div style={{ 
            width: '200px', 
            height: '2px', 
            backgroundColor: 'rgba(255,255,255,0.1)', 
            marginTop: '20px',
            position: 'relative',
            overflow: 'hidden'
        }}>
            <div style={{
                position: 'absolute',
                top: 0,
                left: 0,
                height: '100%',
                backgroundColor: '#c85716',
                width: '100%',
                animation: 'loadingProgress 1.5s infinite ease-in-out'
            }} />
        </div>
      </div>

      <style>{`
        @keyframes pulseLogo {
          0%, 100% { transform: scale(1); opacity: 0.8; }
          50% { transform: scale(1.05); opacity: 1; }
        }
        @keyframes loadingProgress {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(100%); }
        }
      `}</style>
    </div>
  );
};

export default BrandStudioLoader;
